import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MobileBeanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeight, setSelectedWeight] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('aboutBeans');
  const [cartMessage, setCartMessage] = useState('');
  const [randomBeans, setRandomBeans] = useState([]);

  // Redirect desktop users to catalogue with hash
  useEffect(() => {
    const checkAndRedirect = () => {
      const isDesktop = window.innerWidth > 900;
      if (isDesktop) {
        navigate(`/catalogue#${id}`, { replace: true });
      }
    };
    
    // Check on mount
    checkAndRedirect();
    
    // Check on resize
    window.addEventListener('resize', checkAndRedirect);
    
    return () => {
      window.removeEventListener('resize', checkAndRedirect);
    };
  }, [id, navigate]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const productData = {
            id: docSnap.id,
            ...docSnap.data()
          };
          setProduct(productData);
          
          // Set default selections
          let availableWeights, availableTypes;
          if (productData.weightPrices) {
            availableWeights = [...new Set(productData.weightPrices.map(wp => wp.label))].filter(w => w);
            availableTypes = [...new Set(productData.weightPrices.map(wp => wp.type))].filter(t => t);
          }
          if (!availableWeights?.length) {
            availableWeights = productData.weights || productData.beanWeights || ['250'];
          }
          if (!availableTypes?.length) {
            availableTypes = productData.types || productData.beanTypes || ['filter'];
          }
          
          setSelectedWeight(availableWeights[0]);
          setSelectedType(availableTypes[0]);
        } else {
          navigate('/catalogue');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate('/catalogue');
      } finally {
        setLoading(false);
      }
    };

    const fetchRandomBeans = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'products'));
        const allProducts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter coffee products and exclude current product
        const coffeeProducts = allProducts.filter(product => 
          (product.category === 'coffee' || product.shopCategory === 'coffee') &&
          !product.isDrip && 
          product.id !== id
        );
        
        // Get 4 random beans for "You might also like"
        const shuffled = [...coffeeProducts].sort(() => 0.5 - Math.random());
        setRandomBeans(shuffled.slice(0, 4));
        
      } catch (error) {
        console.error('Error fetching random beans:', error);
      }
    };

    if (id) {
      fetchProduct();
      fetchRandomBeans();
    }
  }, [id, navigate]);

  // Scroll to top when component mounts or product changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const handleAddToCart = () => {
    if (!selectedWeight || !selectedType) {
      setCartMessage('Please select weight and type');
      return;
    }

    const price = getPrice();
    const cartItem = {
      id: product.id,
      name: product.name,
      image: product.image,
      weight: selectedWeight,
      type: selectedType,
      quantity: quantity,
      price: typeof price === 'number' ? price : parseFloat(price) || 0,
      origin: product.origin || 'Premium Coffee'
    };

    addToCart(cartItem);
    setCartMessage('Added to cart!');
    setTimeout(() => setCartMessage(''), 3000);
  };

  const getPrice = () => {
    let price = 0;
    
    if (product?.weightPrices) {
      const priceObj = product.weightPrices.find(wp => 
        wp.label === selectedWeight && wp.type === selectedType
      );
      price = priceObj ? priceObj.price : product.price || 0;
    } else {
      price = product?.price || 0;
    }
    
    // Ensure price is always a number
    return typeof price === 'number' ? price : parseFloat(price) || 0;
  };

  const getAvailableWeights = () => {
    if (product?.weightPrices) {
      return [...new Set(product.weightPrices.map(wp => wp.label))].filter(w => w);
    }
    return product?.weights || product?.beanWeights || ['250'];
  };

  const getAvailableTypes = () => {
    if (product?.weightPrices) {
      return [...new Set(product.weightPrices.map(wp => wp.type))].filter(t => t);
    }
    return product?.types || product?.beanTypes || ['filter'];
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Product not found</div>
      </div>
    );
  }

  return (
    <div className="mobile-bean-details">
      <Header />
      
      {/* Back Button */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #e0e0e0',
        background: '#fff'
      }}>
        <button 
          onClick={() => navigate('/catalogue')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '16px',
            color: '#333',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Shop
        </button>
      </div>

      {/* Product Image */}
      <div style={{ 
        padding: '20px', 
        background: '#f8f8f8',
        textAlign: 'center'
      }}>
        <img 
          src={activeTab === 'producer' && product.producerImage ? product.producerImage : product.image} 
          alt={activeTab === 'producer' ? `${product.producer} producer` : product.name}
          style={{
            width: activeTab === 'producer' && product.producerImage ? '320px' : '200px',
            height: activeTab === 'producer' && product.producerImage ? '320px' : '200px',
            objectFit: 'contain',
            borderRadius: '8px',
            transition: 'all 0.3s ease'
          }}
        />
      </div>

      {/* Product Info */}
      <div style={{ padding: '20px', background: '#fff' }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          margin: '0 0 8px 0',
          color: '#333'
        }}>
          {product.name}
        </h1>
        
        <p style={{ 
          fontSize: '16px', 
          color: '#666', 
          margin: '0 0 16px 0' 
        }}>
          {product.origin || 'Premium Coffee'}
        </p>

        <div style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#333',
          marginBottom: '20px'
        }}>
          QAR {getPrice()}
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '0' }}>
            <button
              onClick={() => setActiveTab('aboutBeans')}
              style={{
                padding: '12px 16px',
                background: 'none',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'aboutBeans' ? '#333' : '#666',
                borderBottom: activeTab === 'aboutBeans' ? '2px solid #333' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              ABOUT BEANS
            </button>
            
            {product.producer && (
              <button
                onClick={() => setActiveTab('producer')}
                style={{
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: activeTab === 'producer' ? '#333' : '#666',
                  borderBottom: activeTab === 'producer' ? '2px solid #333' : '2px solid transparent',
                  cursor: 'pointer'
                }}
              >
                PRODUCER
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div style={{ marginBottom: '20px' }}>
          {activeTab === 'aboutBeans' && (
            <div>
              {product.region && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Region:</strong> {product.region}
                </div>
              )}
              {product.altitude && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Altitude:</strong> {product.altitude}
                </div>
              )}
              {product.process && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Process:</strong> {product.process}
                </div>
              )}
              {product.varietal && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Varietal:</strong> {product.varietal}
                </div>
              )}
              {product.notes && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Notes:</strong> {product.notes}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'producer' && (
            <div>
              {product.producer && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Producer:</strong> {product.producer}
                </div>
              )}
              {product.story && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Story:</strong> {product.story}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Options */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Weight:
            </label>
            <select
              value={selectedWeight}
              onChange={(e) => setSelectedWeight(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              {getAvailableWeights().map(weight => (
                <option key={weight} value={weight}>{weight}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Type:
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              {getAvailableTypes().map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Quantity:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #ddd',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                -
              </button>
              <span style={{ fontSize: '18px', fontWeight: '500' }}>{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #ddd',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          style={{
            width: '100%',
            padding: '16px',
            background: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          ADD TO CART - QAR {(getPrice() * quantity).toFixed(2)}
        </button>

        {/* Cart Message */}
        {cartMessage && (
          <div style={{ 
            padding: '12px', 
            background: cartMessage.includes('Added') ? '#d4edda' : '#f8d7da',
            color: cartMessage.includes('Added') ? '#155724' : '#721c24',
            borderRadius: '4px',
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {cartMessage}
          </div>
        )}
      </div>

      {/* You might also like Section */}
      {randomBeans.length > 0 && (
        <div style={{ 
          padding: '20px 0', 
          background: '#fff',
          borderTop: '1px solid #e0e0e0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '0 20px',
            marginBottom: '16px'
          }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              margin: '0',
              color: '#333'
            }}>
              YOU MIGHT ALSO LIKE
            </h3>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              fontSize: '12px', 
              color: '#999'
            }}>
              <span>Swipe</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex',
            overflowX: 'auto',
            gap: '12px',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '8px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' }
          }}>
            {randomBeans.map((bean, index) => (
              <div 
                key={bean.id}
                onClick={() => navigate(`/bean/${bean.id}`)}
                style={{
                  minWidth: '140px',
                  background: '#f8f8f8',
                  borderRadius: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                  position: 'relative',
                  flexShrink: 0,
                  // Add shadow to last item to indicate more content
                  boxShadow: index === randomBeans.length - 1 ? '4px 0 8px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                <img 
                  src={bean.image} 
                  alt={bean.name}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    marginBottom: '8px'
                  }}
                />
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  margin: '0 0 4px 0',
                  color: '#333',
                  lineHeight: '1.2'
                }}>
                  {bean.name}
                </h4>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  margin: '0 0 4px 0',
                  lineHeight: '1.2'
                }}>
                  {bean.origin || 'Premium Coffee'}
                </p>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#333',
                  margin: '0'
                }}>
                  QAR {bean.price}
                </p>
              </div>
            ))}
            
            {/* Visual indicator for more content */}
            <div style={{
              minWidth: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ccc',
              fontSize: '24px',
              flexShrink: 0
            }}>
              →
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MobileBeanDetails;